import {
  Avatar,
  Box,
  Button,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import { LanguageContext } from "../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { getApp } from "firebase/app";
import {
  getStripePayments,
  getCurrentUserSubscriptions,
} from "@invertase/firestore-stripe-payments";

const stripeApp = getApp();
const stripePayments = getStripePayments(stripeApp, {
  productsCollection: "products",
  customersCollection: "customers",
});

export default function HeaderBar({
  title,
  subtitle,
  showSearch = false,
  searchValue = "",
  onSearchChange = () => {},
  extraContent = null,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();

  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [userName, setUserName] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Charger profil
  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setPhotoUrl(data.photoUrl || null);
      setUserName(data.name || "");
    });
    return () => unsub();
  }, [currentUser]);

  // Charger notifications
  useEffect(() => {
    if (!currentUser) return;
    const notifCol = collection(
      db,
      "profilsUsers",
      currentUser.uid,
      "notifications"
    );
    const unsub = onSnapshot(notifCol, (snap) => {
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [currentUser]);

  // Vérifie l’abonnement premium
  useEffect(() => {
    if (!currentUser) return;
    getCurrentUserSubscriptions(stripePayments, { status: "active" })
      .then((subs) => setIsPremium(subs.length > 0))
      .catch(console.error);
  }, [currentUser]);

  // Gestion notifications
  const handleBellClick = (e) => setAnchorNotif(e.currentTarget);
  const handleBellClose = () => setAnchorNotif(null);
  const handleProfileClick = (e) => setAnchorProfile(e.currentTarget);
  const handleProfileClose = () => setAnchorProfile(null);

  const markAsRead = async (nid) => {
    if (!currentUser) return;
    await updateDoc(
      doc(db, "profilsUsers", currentUser.uid, "notifications", nid),
      { read: true }
    );
  };

  const clearReadNotifications = async () => {
    if (!currentUser) return;
    const toDelete = notifications.filter((n) => n.read);
    await Promise.all(
      toDelete.map((n) =>
        deleteDoc(
          doc(db, "profilsUsers", currentUser.uid, "notifications", n.id)
        )
      )
    );
  };

  const sortedNotifications = [...notifications].sort(
    (a, b) => (b?.timestamp?.seconds ?? 0) - (a?.timestamp?.seconds ?? 0)
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        color: "text.primary",
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        backdropFilter: theme.palette.mode === "light" ? "blur(4px)" : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
        {/* Title & optional subtitle */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Search bar */}
        {showSearch && (
          <Box sx={{ flex: 1, maxWidth: 400 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={translate("SearchPlaceholder") || "Search..."}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.background.default,
                  "& fieldset": { borderColor: theme.palette.divider },
                  "&:hover fieldset": { borderColor: theme.palette.primary.main },
                },
              }}
            />
          </Box>
        )}

        {/* Extra content (ex: filtres Agents) */}
        {extraContent && <Box sx={{ ml: 1, flexShrink: 0 }}>{extraContent}</Box>}
      </Box>

      {/* Right side: notifications + profile */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <IconButton onClick={handleBellClick}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>

        {/* Menu notifications */}
        <Menu
          anchorEl={anchorNotif}
          open={Boolean(anchorNotif)}
          onClose={handleBellClose}
          PaperProps={{
            sx: {
              maxHeight: "50vh",
              width: 320,
              bgcolor: "background.paper",
              color: "text.primary",
            },
          }}
        >
          {sortedNotifications.length === 0 ? (
            <MenuItem disabled>
              <ListItemText primary={translate("NoNotifications")} />
            </MenuItem>
          ) : (
            <>
              {sortedNotifications.map((n, idx) => (
                <MenuItem
                  key={n.id}
                  selected={!n.read}
                  onClick={async () => {
                    await markAsRead(n.id);
                    handleBellClose();
                    if (n.link) navigate(n.link);
                  }}
                  sx={{
                    borderBottom:
                      idx !== sortedNotifications.length - 1
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                  }}
                >
                  <ListItemText
                    primary={n.message}
                    secondary={
                      n.timestamp
                        ? new Date(n.timestamp.seconds * 1000).toLocaleString()
                        : ""
                    }
                  />
                </MenuItem>
              ))}

              {sortedNotifications.some((n) => n.read) && (
                <>
                  <Divider />
                  <MenuItem onClick={clearReadNotifications}>
                    <ListItemText
                      primary={translate("ClearReadNotifications")}
                    />
                  </MenuItem>
                </>
              )}
            </>
          )}
        </Menu>

        {/* Avatar + menu profil */}
        <Button
          onClick={handleProfileClick}
          startIcon={
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                isPremium ? (
                  <StarIcon sx={{ color: "gold", fontSize: 14 }} />
                ) : null
              }
            >
              <Avatar
                src={photoUrl || undefined}
                alt={userName}
                sx={{ width: 32, height: 32 }}
              />
            </Badge>
          }
          endIcon={<ArrowDropDownIcon fontSize="small" />}
          sx={{ textTransform: "none", color: "inherit" }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: "0.875rem",
              maxWidth: 100,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {userName}
          </Typography>
        </Button>

        <Menu
          anchorEl={anchorProfile}
          open={Boolean(anchorProfile)}
          onClose={handleProfileClose}
          PaperProps={{
            sx: {
              bgcolor: "background.paper",
              color: "text.primary",
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/app/profile-user");
            }}
          >
            <PersonIcon fontSize="small" sx={{ mr: 1 }} />
            {translate("Profile")}
          </MenuItem>

          {!isPremium && (
            <MenuItem
              onClick={() => {
                handleProfileClose();
                navigate("/app/premium");
              }}
            >
              <StarIcon fontSize="small" sx={{ mr: 1 }} />
              {translate("GoPremium")}
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              handleProfileClose();
              setOpenLogoutConfirm(true);
            }}
          >
            <ExitToAppIcon fontSize="small" sx={{ mr: 1 }} />
            {translate("Logout")}
          </MenuItem>
        </Menu>
      </Box>

      {/* Dialogue de déconnexion */}
      <Dialog
        open={openLogoutConfirm}
        onClose={() => setOpenLogoutConfirm(false)}
      >
        <DialogTitle>{translate("ConfirmLogout")}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenLogoutConfirm(false)}>
            {translate("Cancel")}
          </Button>
          <Button
            onClick={async () => {
              setOpenLogoutConfirm(false);
              await logOut();
              navigate("/login");
            }}
            color="error"
          >
            {translate("Logout")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
